import React from 'react'
import { useTw } from '../Styles'
import classNames from 'classnames'
import { RootNodeInput as Root } from '../../api/types'
import {
  getFormats,
  TextFormatType,
  EditorThemeClasses as Theme,
} from './helpers'

export type InlineNodeProps = {
  item: Root['children'][0]['children'][0]
  theme: Theme
}

function formatClasses(type: TextFormatType | string, theme: Theme) {
  const flag = typeof type === 'string' ? Number(type) : type
  const format = Number.isNaN(flag) ? null : getFormats(flag)

  if (!format) return theme.text.base

  return classNames(
    theme.text.base,
    format.bold && theme.text.bold,
    format.code && theme.text.code,
    format.italic && theme.text.italic,
    format.strikethrough && theme.text.strikethrough,
    format.subscript && theme.text.subscript,
    format.superscript && theme.text.superscript,
    format.underline && theme.text.underline
  )
}

export function LineRenderer({
  item,
  theme,
  nodes,
}: InlineNodeProps & {
  nodes?: Record<string, (props: InlineNodeProps) => JSX.Element>
}) {
  if (nodes?.[item.type]) {
    const Node = nodes[item.type]
    return <Node item={item} theme={theme} />
  }

  switch (item.type) {
    case 'link':
      return (
        <a
          className={theme.link}
          href={item.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {item.children.map((i, k) => (
            <LineRenderer key={k} item={i} theme={theme} />
          ))}
        </a>
      )

    case 'image':
      return (
        <div className={theme.image}>
          <div className="relative">
            <img src={item.src} className={theme.imageTag} />
          </div>
        </div>
      )

    case 'linebreak':
      return <br />

    case 'text':
      return (
        <span className={formatClasses(item.format, theme)}>{item.text}</span>
      )

    default:
      return null
  }
}

export function BlockRenderer({
  item,
  theme,
  inlineNodes,
}: {
  item: Root['children'][0]
  theme: Theme
  inlineNodes?: Record<string, (props: InlineNodeProps) => JSX.Element>
}) {
  const indent = 20 * (item.indent || 0)
  const children = item?.children?.map((_, i) => (
    <LineRenderer key={i} item={_} theme={theme} nodes={inlineNodes} />
  ))

  switch (item.type) {
    case 'paragraph':
      return (
        <p className={theme.paragraph} style={{ paddingInlineStart: indent }}>
          {children}
        </p>
      )

    case 'heading':
      return React.createElement(
        item.tag,
        {
          style: { paddingInlineStart: indent },
          className: theme?.heading?.[item.tag as keyof Theme['heading']],
        },
        children
      )

    case 'quote':
      return (
        <blockquote
          className={theme.quote}
          style={{ paddingInlineStart: indent }}
        >
          {children}
        </blockquote>
      )

    case 'codeblock':
      return (
        <div className={theme.codeblock} style={{ paddingInlineStart: indent }}>
          {children}
        </div>
      )

    case 'iframe':
      return (
        <iframe
          frameBorder="0"
          className={theme.embedBlockFrame}
          src={`${item.src}?title=0&byline=0&portrait=0`}
        />
      )

    case 'list':
      return React.createElement(
        item.tag,
        {
          style: { paddingInlineStart: indent },
          className: theme.list[item.tag as 'ul' | 'ol'],
        },
        item.children.map((_) => (
          <li key={_.value}>
            {_.children.map((el, key) => (
              <LineRenderer key={key} item={el} theme={theme} />
            ))}
          </li>
        ))
      )

    default:
      return null
  }
}

export function StaticRenderer({
  content,
  theme,
  inlineNodes,
}: {
  content: Root
  theme?: Theme
  inlineNodes?: Record<string, (props: InlineNodeProps) => JSX.Element>
}) {
  const tw = useTw()

  return (
    <>
      <>
        {content.children.map((_, i) => (
          <BlockRenderer
            key={i}
            item={_}
            inlineNodes={inlineNodes}
            theme={{
              ...(theme || {}),
              text: {
                ...(theme?.text || {}),
                italic: tw`italic`,
                bold: tw`font-semibold`,
                underline: tw`underline`,
                code: tw`bg-gray-200 rounded-sm`,
              },
              link: tw`text-deep-purple-600`,
            }}
          />
        ))}
      </>
    </>
  )
}
